import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '@/store'
import {
    fetchNotificationPreferences,
    updateNotificationPreferences
} from '@/store/slices/notificationsSlice'
import { showSnackbar } from '@/store/slices/uiSlice'
import { NotificationPreferences as NotificationPreferencesType } from '@/types/notification'
import {
    FiBell,
    FiVolume2,
    FiVolumeX,
    FiCheck,
    FiSave,
    FiMail,
    FiSmartphone,
    FiClock,
    FiToggleLeft,
    FiToggleRight
} from 'react-icons/fi'

type NotificationChannel = 'email' | 'push' | 'inApp'
type NotificationChannelType = keyof NotificationPreferencesType['email']

interface NotificationPreferencesProps {
    onClose?: () => void
}

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ onClose }) => {
    const dispatch = useDispatch<AppDispatch>()
    const { preferences, preferencesLoading } = useSelector((state: RootState) => state.notifications)

    const [localPreferences, setLocalPreferences] = useState<Partial<NotificationPreferencesType>>(() => {
        // Initialize with preferences from Redux store if available, otherwise start with empty object
        // The API will provide defaults when none exist, so we don't need hardcoded defaults here
        if (preferences) {
            console.log('🔧 Initializing local state with existing preferences from Redux store:', preferences)
            return preferences
        }
        console.log('🔧 Initializing local state with empty object, waiting for API defaults')
        return {}
    })

    const [hasChanges, setHasChanges] = useState(false)

    useEffect(() => {
        console.log('🚀 NotificationPreferences component mounted, fetching preferences...')
        dispatch(fetchNotificationPreferences())
    }, [dispatch])

    useEffect(() => {
        if (preferences) {
            console.log('🔄 Syncing server preferences to local state:', preferences)
            setLocalPreferences(preferences)
            setHasChanges(false) // Reset changes flag when syncing with server
        }
    }, [preferences])

    const handleChannelToggle = (channel: NotificationChannel, notificationType: NotificationChannelType) => {
        setLocalPreferences(prev => ({
            ...(() => {
                const channelPreferences = (prev[channel] || {}) as Partial<Record<NotificationChannelType, boolean>>
                return {
                    ...prev,
                    [channel]: {
                        ...channelPreferences,
                        [notificationType]: !channelPreferences[notificationType]
                    }
                }
            })()
        }))
        setHasChanges(true)
    }

    const handleFrequencyChange = (frequency: 'immediate' | 'daily' | 'weekly') => {
        setLocalPreferences(prev => ({
            ...prev,
            frequency
        }))
        setHasChanges(true)
    }

    const handleQuietHoursToggle = () => {
        setLocalPreferences(prev => ({
            ...prev,
            quietHours: {
                ...prev.quietHours!,
                enabled: !prev.quietHours!.enabled
            }
        }))
        setHasChanges(true)
    }

    const handleQuietHoursTimeChange = (field: 'start' | 'end', value: string) => {
        setLocalPreferences(prev => ({
            ...prev,
            quietHours: {
                ...prev.quietHours!,
                [field]: value
            }
        }))
        setHasChanges(true)
    }

    const handleSave = async () => {
        try {
            // Filter out MongoDB metadata fields that shouldn't be sent in updates
            const cleanPreferences = {
                frequency: localPreferences.frequency,
                language: localPreferences.language,
                timezone: localPreferences.timezone,
                digestEnabled: localPreferences.digestEnabled,
                digestTime: localPreferences.digestTime,
                maxDailyNotifications: localPreferences.maxDailyNotifications,
                soundEnabled: localPreferences.soundEnabled,
                vibrationEnabled: localPreferences.vibrationEnabled,
                emailFormat: localPreferences.emailFormat,
                email: localPreferences.email,
                push: localPreferences.push,
                inApp: localPreferences.inApp,
                quietHours: localPreferences.quietHours
            }

            console.log('💾 Saving preferences (cleaned):', cleanPreferences)
            const result = await dispatch(updateNotificationPreferences(cleanPreferences)).unwrap()
            console.log('✅ Save result:', result)
            dispatch(showSnackbar({
                message: 'Notification preferences updated successfully',
                type: 'success',
                duration: 4000
            }))
            setHasChanges(false)
            if (onClose) onClose()
        } catch (error) {
            console.error('❌ Save error:', error)
            dispatch(showSnackbar({
                message: 'Failed to update preferences. Please try again.',
                type: 'error',
                duration: 6000
            }))
        }
    }

    const notificationTypes: Array<{ key: NotificationChannelType; label: string; description: string }> = [
        { key: 'gigApplications', label: 'Gig Applications', description: 'New applications to your gigs' },
        { key: 'applicationStatus', label: 'Application Status', description: 'Updates on your job applications' },
        { key: 'messages', label: 'Messages', description: 'New messages from users' },
        { key: 'gigReminders', label: 'Gig Reminders', description: 'Reminders about upcoming gigs' },
        { key: 'payments', label: 'Payments', description: 'Payment notifications' },
        { key: 'ratings', label: 'Ratings & Reviews', description: 'New ratings and reviews' },
        { key: 'systemUpdates', label: 'System Updates', description: 'Platform updates and maintenance' },
        { key: 'promotional', label: 'Promotional', description: 'Marketing and promotional content' },
        { key: 'accountSecurity', label: 'Security Alerts', description: 'Account security notifications' },
        { key: 'welcome', label: 'Welcome Messages', description: 'Welcome and onboarding messages' },
        { key: 'emailVerification', label: 'Email Verification', description: 'Email verification requests' },
        { key: 'loginAlerts', label: 'Login Alerts', description: 'New login notifications' },
        { key: 'profileUpdates', label: 'Profile Updates', description: 'Profile change notifications' }
    ]

    const frequencyOptions = [
        { value: 'immediate', label: 'Immediate', icon: <FiBell className="w-4 h-4" /> },
        { value: 'daily', label: 'Daily Digest', icon: <FiClock className="w-4 h-4" /> },
        { value: 'weekly', label: 'Weekly Digest', icon: <FiClock className="w-4 h-4" /> }
    ]

    // Show loading state while preferences are loading or local preferences are empty
    if (preferencesLoading && !preferences) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                    <div className="space-y-3">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
                    </div>
                </div>
            </div>
        )
    }

    // Show loading state if local preferences are not yet populated (waiting for API defaults)
    if (!localPreferences || Object.keys(localPreferences).length === 0) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                    <div className="space-y-3">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-800">
            <div className="flex-1 w-full px-3 sm:px-4 py-2 sm:py-3 pb-4 sm:pb-6">
                <div className="space-y-2 sm:space-y-3">
                    {/* Notification Frequency */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 sm:p-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                            <FiClock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">Notification Frequency</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                            {frequencyOptions.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => handleFrequencyChange(option.value as any)}
                                    className={`flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg border-2 transition-colors text-xs sm:text-sm md:text-base ${localPreferences.frequency === option.value
                                        ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                                        }`}
                                >
                                    {option.icon}
                                    <span className="font-medium flex-1 text-center sm:text-left text-xs sm:text-sm md:text-base">{option.label}</span>
                                    {localPreferences.frequency === option.value && (
                                        <FiCheck className="w-3 h-3 sm:w-4 sm:h-4 ml-auto flex-shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Quiet Hours */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 sm:p-3">
                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <FiVolume2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
                                <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">Quiet Hours</h3>
                            </div>
                            <button
                                onClick={handleQuietHoursToggle}
                                className="focus:outline-none"
                            >
                                {localPreferences.quietHours?.enabled ? (
                                    <FiToggleRight className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 dark:text-purple-400" />
                                ) : (
                                    <FiToggleLeft className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-500" />
                                )}
                            </button>
                        </div>

                        {localPreferences.quietHours?.enabled && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                <div>
                                    <label className="block text-xs sm:text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Start Time
                                    </label>
                                    <input
                                        type="time"
                                        value={localPreferences.quietHours.start}
                                        onChange={(e) => handleQuietHoursTimeChange('start', e.target.value)}
                                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent text-gray-900 dark:text-white text-xs sm:text-sm md:text-base"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        End Time
                                    </label>
                                    <input
                                        type="time"
                                        value={localPreferences.quietHours.end}
                                        onChange={(e) => handleQuietHoursTimeChange('end', e.target.value)}
                                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent text-gray-900 dark:text-white text-xs sm:text-sm md:text-base"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notification Types */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 sm:p-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                            <FiBell className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
                            <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">Notification Types</h3>
                        </div>

                        <div className="space-y-2 sm:space-y-2">
                            {/* Header - Desktop only */}
                            <div className="hidden sm:grid grid-cols-12 gap-4 items-center pb-2 border-b border-gray-200 dark:border-gray-600">
                                <div className="col-span-4">
                                    <h4 className="font-medium text-xs sm:text-sm md:text-base text-gray-900 dark:text-white">Notification Type</h4>
                                </div>
                                <div className="col-span-2 text-center">
                                    <FiMail className="w-5 h-5 mx-auto text-blue-600 dark:text-blue-400" />
                                    <span className="text-xs sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">Email</span>
                                </div>
                                <div className="col-span-2 text-center">
                                    <FiSmartphone className="w-5 h-5 mx-auto text-green-600 dark:text-green-400" />
                                    <span className="text-xs sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">Push</span>
                                </div>
                                <div className="col-span-2 text-center">
                                    <FiBell className="w-5 h-5 mx-auto text-purple-600 dark:text-purple-400" />
                                    <span className="text-xs sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">In-App</span>
                                </div>
                            </div>

                            {/* Notification Types */}
                            {notificationTypes.map((type) => (
                                <div key={type.key}>
                                    {/* Desktop Layout */}
                                    <div className="hidden sm:grid grid-cols-12 gap-4 items-center py-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg px-2">
                                        <div className="col-span-4">
                                            <h5 className="font-medium text-xs sm:text-sm md:text-base text-gray-900 dark:text-white">{type.label}</h5>
                                            <p className="text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400">{type.description}</p>
                                        </div>

                                        {/* Email Toggle */}
                                        <div className="col-span-2 text-center">
                                            <button
                                                onClick={() => handleChannelToggle('email', type.key)}
                                                className="focus:outline-none"
                                            >
                                                {localPreferences.email?.[type.key as keyof typeof localPreferences.email] ? (
                                                    <FiToggleRight className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                                ) : (
                                                    <FiToggleLeft className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                                                )}
                                            </button>
                                        </div>

                                        {/* Push Toggle */}
                                        <div className="col-span-2 text-center">
                                            {type.key !== 'promotional' && (
                                                <button
                                                    onClick={() => handleChannelToggle('push', type.key)}
                                                    className="focus:outline-none"
                                                >
                                                    {localPreferences.push?.[type.key as keyof typeof localPreferences.push] ? (
                                                        <FiToggleRight className="w-6 h-6 text-green-600 dark:text-green-400" />
                                                    ) : (
                                                        <FiToggleLeft className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                                                    )}
                                                </button>
                                            )}
                                        </div>

                                        {/* In-App Toggle */}
                                        <div className="col-span-2 text-center">
                                            {type.key !== 'promotional' && (
                                                <button
                                                    onClick={() => handleChannelToggle('inApp', type.key)}
                                                    className="focus:outline-none"
                                                >
                                                    {localPreferences.inApp?.[type.key as keyof typeof localPreferences.inApp] ? (
                                                        <FiToggleRight className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                                                    ) : (
                                                        <FiToggleLeft className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Mobile Layout */}
                                    <div className="sm:hidden bg-white dark:bg-gray-600 rounded-lg p-3 border border-gray-200 dark:border-gray-500">
                                        <div className="mb-3">
                                            <h5 className="font-medium text-xs sm:text-sm md:text-base text-gray-900 dark:text-white">{type.label}</h5>
                                            <p className="text-xs sm:text-xs md:text-sm text-gray-500 dark:text-gray-400">{type.description}</p>
                                        </div>

                                        <div className="space-y-2">
                                            {/* Email Toggle */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <FiMail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                    <span className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300">Email</span>
                                                </div>
                                                <button
                                                    onClick={() => handleChannelToggle('email', type.key)}
                                                    className="focus:outline-none"
                                                >
                                                    {localPreferences.email?.[type.key as keyof typeof localPreferences.email] ? (
                                                        <FiToggleRight className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                    ) : (
                                                        <FiToggleLeft className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                                    )}
                                                </button>
                                            </div>

                                            {/* Push Toggle */}
                                            {type.key !== 'promotional' && (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <FiSmartphone className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                        <span className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300">Push</span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleChannelToggle('push', type.key)}
                                                        className="focus:outline-none"
                                                    >
                                                        {localPreferences.push?.[type.key as keyof typeof localPreferences.push] ? (
                                                            <FiToggleRight className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                        ) : (
                                                            <FiToggleLeft className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                                        )}
                                                    </button>
                                                </div>
                                            )}

                                            {/* In-App Toggle */}
                                            {type.key !== 'promotional' && (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <FiBell className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                                        <span className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300">In-App</span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleChannelToggle('inApp', type.key)}
                                                        className="focus:outline-none"
                                                    >
                                                        {localPreferences.inApp?.[type.key as keyof typeof localPreferences.inApp] ? (
                                                            <FiToggleRight className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                                        ) : (
                                                            <FiToggleLeft className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Save Button Footer - Always Visible */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 p-3 sm:p-6 flex justify-center sm:justify-end shadow-lg">
                <button
                    onClick={handleSave}
                    disabled={preferencesLoading || !hasChanges}
                    className={`flex items-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors text-xs sm:text-sm md:text-base font-medium min-w-[140px] justify-center ${hasChanges && !preferencesLoading
                        ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white focus:ring-blue-500 dark:focus:ring-blue-400'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                >
                    <FiSave className="w-4 h-4" />
                    <span>
                        {preferencesLoading
                            ? 'Saving...'
                            : hasChanges
                                ? 'Save Changes'
                                : 'No Changes'
                        }
                    </span>
                </button>
            </div>
        </div>
    )
}

export default NotificationPreferences